import { PatientFilledForm, getFormNames } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService, formatTime } from '@uplift/services';
import { HighRiskAssessment } from './high-risk.component';
import { NormalRiskActionItem } from './normal-risk.component';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ProviderMedicalRole } from '@uplift/types';

export const isHighRisk = (patient: PatientFilledForm) =>
  Object.values(patient.assessments).some(x => x.shouldNotify) && !patient.patientHasPsych;

export type PatientFilledFormProps = {
  patient: PatientFilledForm;
};

export const PatientFilledFormActionItem: React.VFC<PatientFilledFormProps> = ({ patient }) => {
  const me = useCurrentuser();
  const timezone = useTimezone();

  const formKeys = Object.keys(patient.assessments);
  const { data: formNameData, isLoading } = useQuery({
    queryKey: [ 'form-names', formKeys ],
    queryFn: async () => {
      const response = await getFormNames(formKeys);

      return response.data;
    }
  });

  const formNameMap = useMemo(() => {
    if (isLoading) {
      return {};
    }

    return formNameData;
  }, [ formNameData, isLoading ]);

  const appointment = CalendarDateService.date(patient.nextAppointmentAt).tz(timezone);
  const date = appointment.format('MMM Do');
  const time = formatTime(appointment);
  const recommendConsult = Object.values(patient.assessments).some(x => x.shouldNotify);
  const showHighRisk = recommendConsult && !patient.patientHasPsych && me.medicalRole !== ProviderMedicalRole.psychiatrist;

  const props = {
    date,
    formNameMap,
    time,
    patient
  };

  return showHighRisk
    ? <HighRiskAssessment {...props} />
    : <NormalRiskActionItem {...props} recommendConsult={recommendConsult} />;
};
